import { SelectOption } from '@/types/common.types';
import { IconType } from '@/types/icon.types';
import { z } from 'zod';
import { LoggingTargetType } from '../../utils/types';

const LOG_TARGET_TYPE_VALUES = [
  'custom',
  'activemq',
  'apache',
  'clickhouse',
  'elasticsearch',
  'glusterfs',
  'go',
  'iis',
  'java',
  'jeus',
  'kafka',
  'mongo',
  'mysql',
  'nginx',
  'nodejs',
  'oracle',
  'php',
  'postgresql',
  'python',
  'redis',
  'solr',
  'sqlserver',
  'tomcat',
  'WebtoB',
  'weblogic',
  'zookeeper',
  'pod',
  'cubrid',
  'awsAuroraMysql',
  'awsRdsMysql',
  'awsRdsOracle',
  'awsAuroraPostgresql',
  'awsRdsPostgresql',
] as const;

/**
 * Zod schema for LogTargetType validation
 */
export const LogTargetTypeSchema = z.enum(LOG_TARGET_TYPE_VALUES);

/**
 * Log target type inferred from Zod schema
 */
export type LogTargetType = z.infer<typeof LogTargetTypeSchema>;

export type LogCategoryType =
  | ''
  | 'messageQueue'
  | 'application'
  | 'database'
  | 'kubernetes'
  | 'awsAurora'
  | 'awsRds';
export interface LogTargetOption extends SelectOption {
  value: LogTargetType;
  defaultPath: string;
  category: LogCategoryType;
  icon?: IconType;
}

export const getIconURL = (icon: string) =>
  new URL(`/src/common/assets/${icon}.svg`, import.meta.url).href;

export const AWS_DATABASE_VALUE = {
  AURORA_MYSQL: 'awsAuroraMysql',
  RDS_MYSQL: 'awsRdsMysql',
  RDS_ORACLE: 'awsRdsOracle',
  AURORA_POSTGRESQL: 'awsAuroraPostgresql',
  RDS_POSTGRESQL: 'awsRdsPostgresql',
} as const;

export const LOG_TARGET_TYPES = [
  {
    name: 'Custom',
    value: 'custom',
    defaultPath: '',
    category: '',
  },
  {
    name: 'ActiveMQ',
    value: 'activemq',
    defaultPath: '<ACTIVEMQ_BASEDIR>/data/activemq.log',
    category: 'messageQueue',
    icon: 'icon-activemq',
  },
  {
    name: 'Apache',
    value: 'apache',
    defaultPath: '/var/log/apache2/access.log',
    category: 'application',
    icon: 'icon-apache',
  },
  {
    name: 'ClickHouse',
    value: 'clickhouse',
    defaultPath: '/var/log/clickhouse-server/clickhouse-server.log',
    category: 'database',
    icon: 'icon-clickhouse-graphic',
  },
  {
    name: 'Elasticsearch',
    value: 'elasticsearch',
    defaultPath: '/var/log/elasticsearch/elasticsearch.log',
    category: 'database',
    icon: 'icon-elasticsearch',
  },
  {
    name: 'GlusterFS',
    value: 'glusterfs',
    defaultPath: '/var/log/glusterfs/glusterd.log',
    category: 'application',
    icon: 'icon-glusterfs',
  },
  {
    name: 'Go',
    value: 'go',
    defaultPath: '<path_to_your_go_log>.log',
    category: 'application',
    icon: 'icon-golang',
  },
  {
    name: 'IIS',
    value: 'iis',
    defaultPath: 'C:\\inetpub\\logs\\LogFiles\\W3SVC1\\u_ex*',
    category: 'application',
    icon: 'icon-iis',
  },
  {
    name: 'Java',
    value: 'java',
    defaultPath: '<path_to_your_java_log>.log',
    category: 'application',
    icon: 'icon-java',
  },
  {
    name: 'Jeus',
    value: 'jeus',
    defaultPath: '{$SERVER_HOME}/logs/JeusServer.log',
    category: 'application',
    icon: 'icon-jeus-color',
  },
  {
    name: 'Kafka',
    value: 'kafka',
    defaultPath: '/var/log/kafka/server.log',
    category: 'messageQueue',
    icon: 'icon-kafka',
  },
  {
    name: 'MongoDB',
    value: 'mongo',
    defaultPath: '/var/log/mongodb/mongodb.log',
    category: 'database',
    icon: 'icon-mongodb',
  },
  {
    name: 'MySQL',
    value: 'mysql',
    defaultPath: '/var/log/mysqld.log',
    category: 'database',
    icon: 'icon-mysql',
  },
  {
    name: 'Nginx',
    value: 'nginx',
    defaultPath: '/var/log/nginx/access.log',
    category: 'application',
    icon: 'icon-nginx',
  },
  {
    name: 'Node.js',
    value: 'nodejs',
    defaultPath: '<path_to_your_nodejs_log>.log',
    category: 'application',
    icon: 'icon-nodejs',
  },
  {
    name: 'Oracle',
    value: 'oracle',
    defaultPath: '{$ORACLE_BASE}/diag/rdbms/<DB_UNIQUE_NAME>/<ORACLE_SID>/trace/alert<SID>.log',
    category: 'database',
    icon: 'icon-oracle',
  },
  {
    name: 'PHP',
    value: 'php',
    defaultPath: '<path_to_your_php_application_json>.log',
    category: 'application',
    icon: 'icon-php',
  },
  {
    name: 'PostgreSQL',
    value: 'postgresql',
    defaultPath: '/var/log/postgres.log',
    category: 'database',
    icon: 'icon-postgresql',
  },
  {
    name: 'Python',
    value: 'python',
    defaultPath: '<path_to_python_log>.log',
    category: 'application',
    icon: 'icon-python',
  },
  {
    name: 'Redis',
    value: 'redis',
    defaultPath: '/var/log/redis_6379.log',
    category: 'database',
    icon: 'icon-redis',
  },
  {
    name: 'Solr',
    value: 'solr',
    defaultPath: '/var/log/solr/solr.log',
    category: 'application',
    icon: 'icon-solr',
  },
  {
    name: 'SQLServer',
    value: 'sqlserver',
    defaultPath: '/var/opt/mssql/log/errorlog',
    category: 'database',
    icon: 'icon-sqlserver',
  },
  {
    name: 'Tomcat',
    value: 'tomcat',
    defaultPath: '/var/log/tomcat/catalina.out',
    category: 'application',
    icon: 'icon-tomcat',
  },
  {
    name: 'WebtoB',
    value: 'WebtoB',
    defaultPath: '{$WEBTOBDIR}/log/access.log',
    category: 'application',
    icon: 'icon-webtob',
  },
  {
    name: 'WebLogic',
    value: 'weblogic',
    defaultPath: '<DOMAIN_DIR>/servers/<SERVER_NAME>/logs/access.log',
    category: 'application',
    icon: 'icon-weblogic',
  },
  {
    name: 'ZooKeeper',
    value: 'zookeeper',
    defaultPath: '/var/log/zookeeper.log',
    category: 'messageQueue',
    icon: 'icon-zookeeper',
  },
  {
    name: 'Pod',
    value: 'pod',
    defaultPath: 'STDOUT',
    category: 'kubernetes',
    icon: 'icon-k8s',
  },
  {
    name: 'CUBRID',
    value: 'cubrid',
    defaultPath: '',
    category: 'database',
    icon: 'icon-cubrid',
  },
  {
    name: 'AWS Aurora MySQL',
    value: AWS_DATABASE_VALUE.AURORA_MYSQL,
    defaultPath: '',
    category: 'awsAurora',
    icon: 'icon-aws-aurora-and-mysql',
  },
  {
    name: 'AWS RDS MYSQL',
    value: AWS_DATABASE_VALUE.RDS_MYSQL,
    defaultPath: '',
    category: 'awsRds',
    icon: 'icon-aws-rds-and-mysql',
  },
  {
    name: 'AWS RDS Oracle',
    value: AWS_DATABASE_VALUE.RDS_ORACLE,
    defaultPath: '',
    category: 'awsRds',
    icon: 'icon-aws-rds-and-oracle',
  },
  {
    name: 'AWS Aurora Postgresql',
    value: AWS_DATABASE_VALUE.AURORA_POSTGRESQL,
    defaultPath: '/aws/rds/cluster/{DB CLUSTER ID}/postgresql/{log stream}',
    category: 'awsAurora',
    icon: 'icon-aws-aurora-and-postgresql',
  },
  {
    name: 'AWS RDS PostgreSQL',
    value: AWS_DATABASE_VALUE.RDS_POSTGRESQL,
    defaultPath: '/aws/rds/instance/{DB INSTANCE ID}/postgresql/{log stream}',
    category: 'awsRds',
    icon: 'icon-aws-rds-and-postgresql',
  },
] satisfies LogTargetOption[];

export const LOG_TYPE_NAME_MAP: Record<LogTargetType, string> = LOG_TARGET_TYPES.reduce(
  (acc, item) => {
    acc[item.value] = item.name;
    return acc;
  },
  {} as Record<LogTargetType, string>,
);

export const LOG_TYPE_VALUE_MAP: Record<string, LogTargetType> = LOG_TARGET_TYPES.reduce(
  (acc, item) => {
    acc[item.name] = item.value;
    return acc;
  },
  {} as Record<string, LogTargetType>,
);

export const LOG_TARGET_TYPE_MAP_WITHOUT_HOST: Record<LoggingTargetType, LogTargetType[]> =
  LOG_TARGET_TYPES.reduce(
    (acc, item) => {
      const values = [...(acc[item.category] || []), item.value];
      if (item.category !== 'kubernetes' && !values.includes('custom')) {
        values.push('custom');
      }
      acc[item.category] = values;
      return acc;
    },
    {} as Record<LoggingTargetType, LogTargetType[]>,
  );

export const LOG_TARGET_TYPE_MAP: Record<LoggingTargetType, LogTargetType[]> = {
  ...LOG_TARGET_TYPE_MAP_WITHOUT_HOST,
  host: [
    ...LOG_TARGET_TYPES.filter((item) => item.category !== 'kubernetes').map((item) => item.value),
    'custom',
  ],
};

const awsMysqlFormats = [
  {
    name: 'Error',
    value: 'error',
  },
  {
    name: 'General',
    value: 'general',
  },
  {
    name: 'Audit',
    value: 'audit',
  },
  {
    name: 'Slowquery',
    value: 'slowquery',
  },
];
const awsOracleFormats = [
  {
    name: 'Alert',
    value: 'alert',
  },
  {
    name: 'Trace',
    value: 'trace',
  },
  {
    name: 'Audit',
    value: 'audit',
  },
  {
    name: 'Listener',
    value: 'listener',
  },
];

export const supportedAWSLogFormats = {
  [AWS_DATABASE_VALUE.AURORA_MYSQL]: awsMysqlFormats,
  [AWS_DATABASE_VALUE.RDS_MYSQL]: awsMysqlFormats,
  [AWS_DATABASE_VALUE.RDS_ORACLE]: awsOracleFormats,
} as const;

export const supportedCubridLogFormats = [
  {
    name: 'Broker - Error',
    value: 'error',
  },
  {
    name: 'Broker - Access',
    value: 'access',
  },
  {
    name: 'Broker - SQL',
    value: 'sql',
  },
  {
    name: 'Broker - Slow SQL',
    value: 'slowSql',
  },
] as const;

export const getDefaultPathInfo = (type: LogTargetType | string, env: string, format: string) => {
  if (type === AWS_DATABASE_VALUE.RDS_MYSQL || type === AWS_DATABASE_VALUE.RDS_ORACLE) {
    return `/aws/rds/instance/{DB INSTANCE ID}/${format}/{log stream}`;
  }
  if (type === AWS_DATABASE_VALUE.AURORA_MYSQL) {
    return `/aws/rds/cluster/{DB INSTANCE ID}/${format}/{log stream}`;
  }

  if (type === 'cubrid') {
    switch (format) {
      case 'error':
        return '$CUBRID/log/broker/error_log/{broker_name}_{cas_id}.err';
      case 'access':
        return '$CUBRID/log/broker/{broker_name}.access*';
      case 'sql':
        return '$CUBRID/log/broker/sql_log/{broker_name}_{cas_id}.sql.log';
      case 'slowSql':
        return '$CUBRID/log/broker/sql_log/{broker_name}_{cas_id}.slow.log';
      default:
        return '';
    }
  }

  return LOG_TARGET_TYPES.find((item) => item.value === type)?.defaultPath ?? '';
};
